import z from 'zod'
import { type ReactElement } from 'react'
import { Combobox, Group, Text, useCombobox, useMantineColorScheme } from '@mantine/core'
import { type MaterialSymbolType, useTranslation } from '@front/model'
import { MaterialSymbolIcon } from './terros'
import { TButton } from './buttons/button'

export const ThemeButton = (): ReactElement => {
  const { setColorScheme, colorScheme } = useMantineColorScheme()
  const { t } = useTranslation()

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  })

  const currentTheme = themeOptions.find((option) => option.value === colorScheme)
  const currentIcon = currentTheme?.icon || 'light_mode'

  const options = themeOptions.map((option) => {
    return (
      <Combobox.Option value={option.value} key={option.value}>
        <Group gap='sm'>
          <MaterialSymbolIcon icon={option.icon} size={16} />
          <Text>{option.label}</Text>
        </Group>
      </Combobox.Option>
    )
  })

  return (
    <Combobox
      store={combobox}
      width={200}
      position='bottom-start'
      withArrow
      onOptionSubmit={(val) => {
        if (!isColorScheme(val)) return
        setColorScheme(val)
        combobox.closeDropdown()
      }}
    >
      <Combobox.Target>
        <TButton icon={currentIcon} variant='secondary' onClick={() => combobox.toggleDropdown()} />
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>
          <Combobox.Group label='Color Scheme'>{options}</Combobox.Group>
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )
}

const COLOR_SCHEMES = ['light', 'dark', 'auto'] as const
const ColorSchemeSchema = z.enum(COLOR_SCHEMES)
const isColorScheme = (value: string): value is ColorScheme => ColorSchemeSchema.safeParse(value).success
type ColorScheme = z.infer<typeof ColorSchemeSchema>

type ThemeOption = {
  value: ColorScheme
  label: string
  icon: MaterialSymbolType
}

const themeOptions: ThemeOption[] = [
  { value: 'light', label: 'Always light', icon: 'light_mode' },
  { value: 'dark', label: 'Always dark', icon: 'dark_mode' },
  { value: 'auto', label: 'System', icon: 'monitor' },
]
